import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const CHANGE_LABELS = {
  add: 'Add',
  update: 'Update',
  remove: 'Remove',
  unchanged: 'No change',
};

const ChangeSummary = ({ value }) => value ? (
  <Stack spacing={0.25}>
    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{value.title}</Typography>
    {value.localDate ? <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{value.localDate}</Typography> : null}
    {value.body ? <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{value.body}</Typography> : null}
  </Stack>
) : null;

const ReorganizationProposalReview = ({ changes = [], needsDateItems = [], replaceAll = false, applying = false, onApply }) => {
  const actionableChanges = React.useMemo(
    () => changes.filter((change) => change.type !== 'unchanged'),
    [changes]
  );
  const [selected, setSelected] = React.useState({});
  const [overrides, setOverrides] = React.useState({});

  React.useEffect(() => {
    setSelected(Object.fromEntries(actionableChanges.map((change) => [
      change.id,
      replaceAll || change.type !== 'remove',
    ])));
    setOverrides(Object.fromEntries(actionableChanges.map((change) => [
      change.id,
      {
        title: change.proposed?.title || '',
        body: change.proposed?.body || '',
        localDate: change.proposed?.localDate || '',
      },
    ])));
  }, [actionableChanges, replaceAll]);

  const updateOverride = (changeId, field, value) => {
    setOverrides((current) => ({
      ...current,
      [changeId]: { ...(current[changeId] || {}), [field]: value },
    }));
  };

  const selectedCount = actionableChanges.filter((change) => selected[change.id]).length;
  const handleApply = () => onApply(actionableChanges
    .filter((change) => selected[change.id])
    .map((change) => ({
      changeId: change.id,
      overrides: change.type === 'remove' ? {} : overrides[change.id],
    })));

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>{replaceAll ? 'Review organized results' : 'Review proposed changes'}</Typography>
        <Typography sx={{ mt: 0.25, fontSize: '0.8rem', color: 'text.secondary' }}>
          {replaceAll
            ? 'These changes replace only the Timeline results created from this email. Your original email will not change.'
            : 'Choose exactly what Lifelog should change. Removals are never selected automatically.'}
        </Typography>
      </Box>

      {needsDateItems.length ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 800 }}>{needsDateItems.length} update{needsDateItems.length === 1 ? '' : 's'} need a date</Typography>
          {needsDateItems.map((item, index) => <Typography key={`${item.title}-${index}`} sx={{ mt: 0.4, fontSize: '0.8rem', color: 'text.secondary' }}>{item.title}</Typography>)}
        </Paper>
      ) : null}

      {!actionableChanges.length ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Lifelog did not find any changes to make.</Typography>
        </Paper>
      ) : actionableChanges.map((change) => {
        const isSelected = Boolean(selected[change.id]);
        return (
          <Paper key={change.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                {!replaceAll ? <Checkbox
                  checked={isSelected}
                  onChange={(event) => setSelected((current) => ({
                    ...current,
                    [change.id]: event.target.checked,
                  }))}
                  inputProps={{ 'aria-label': `${isSelected ? 'Reject' : 'Approve'} ${CHANGE_LABELS[change.type]} change` }}
                  sx={{ p: 0.25 }}
                /> : null}
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                  <Chip label={CHANGE_LABELS[change.type]} size="small" sx={{ alignSelf: 'flex-start', fontWeight: 800 }} />
                  {change.type === 'update' ? (
                    <>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'text.secondary' }}>CURRENT</Typography>
                      <ChangeSummary value={change.current} />
                      <Divider />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'text.secondary' }}>PROPOSED</Typography>
                    </>
                  ) : null}
                  {change.type === 'remove' ? <ChangeSummary value={change.current} /> : null}
                  {change.type !== 'remove' ? (
                    <Stack spacing={1}>
                      <TextField
                        label="Title"
                        size="small"
                        value={overrides[change.id]?.title || ''}
                        onChange={(event) => updateOverride(change.id, 'title', event.target.value)}
                        disabled={!isSelected || applying}
                        fullWidth
                      />
                      <TextField
                        label="Date"
                        type="date"
                        size="small"
                        value={overrides[change.id]?.localDate || ''}
                        onChange={(event) => updateOverride(change.id, 'localDate', event.target.value)}
                        disabled={!isSelected || applying}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="Details"
                        multiline
                        minRows={2}
                        value={overrides[change.id]?.body || ''}
                        onChange={(event) => updateOverride(change.id, 'body', event.target.value)}
                        disabled={!isSelected || applying}
                        fullWidth
                      />
                    </Stack>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        );
      })}

      {actionableChanges.length ? (
        <Button variant="contained" onClick={handleApply} disabled={applying || selectedCount === 0}>
          {applying ? 'Applying changes…' : (replaceAll ? 'Replace organized results' : `Apply selected changes (${selectedCount})`)}
        </Button>
      ) : null}
    </Stack>
  );
};

export default ReorganizationProposalReview;
